import { useEffect, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { NavIcon } from '@/components/nav/NavIcon';
import type { NavMenuNode } from '@/api/platform-nav-menu';
import { useUIStore } from '@/store/useUIStore';
import { cn } from '@/lib/utils';

/** 角色可勾选的侧栏 path：去掉 BUTTON；目录仅作分组；无 path 的菜单当作父级 */
export function filterNavTreeForRolePicker(nodes: NavMenuNode[]): NavMenuNode[] {
  const out: NavMenuNode[] = [];
  for (const n of nodes) {
    if (n.menuType === 'BUTTON') continue;
    const kids = filterNavTreeForRolePicker(n.children ?? []);
    if (n.menuType === 'DIRECTORY') {
      if (kids.length > 0) out.push({ ...n, children: kids });
      continue;
    }
    if (n.menuType === 'MENU' || n.menuType === 'EXTERNAL') {
      if (n.path) {
        out.push({ ...n, children: kids });
      } else if (kids.length > 0) {
        out.push({ ...n, children: kids });
      }
    }
  }
  return out;
}

function collectExpandableIds(nodes: NavMenuNode[], s = new Set<string>()) {
  for (const n of nodes) {
    if (n.children?.length) {
      s.add(n.id);
      collectExpandableIds(n.children, s);
    }
  }
  return s;
}

type Props = {
  tree: NavMenuNode[];
  value: Set<string>;
  onToggle: (path: string) => void;
  disabled?: boolean;
  /** 租户抽屉用 fuchsia 强调 */
  accent?: 'cyan' | 'fuchsia';
};

export function RoleMenuTreeCheckboxes({
  tree,
  value,
  onToggle,
  disabled,
  accent = 'cyan',
}: Props) {
  const isLight = useUIStore((s) => s.theme === 'light');
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    collectExpandableIds(tree),
  );

  useEffect(() => {
    setExpanded(collectExpandableIds(tree));
  }, [tree]);

  const chevronOpen = accent === 'fuchsia' ? 'text-fuchsia-300' : 'text-cyan-300';
  const chevronOpenLight = accent === 'fuchsia' ? 'text-fuchsia-600' : 'text-cyan-600';

  function renderNodes(nodes: NavMenuNode[], depth: number): ReactNode {
    return nodes.map((n) => {
      const hasChildren = (n.children?.length ?? 0) > 0;
      const selectable =
        (n.menuType === 'MENU' || n.menuType === 'EXTERNAL') && Boolean(n.path);
      const open = expanded.has(n.id);

      return (
        <div key={n.id} className="select-none">
          <div
            className="flex items-start gap-1 py-0.5"
            style={{ paddingLeft: depth * 14 }}
          >
            {hasChildren ? (
              <button
                type="button"
                className={cn(
                  isLight
                    ? 'mt-0.5 shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                    : 'mt-0.5 shrink-0 rounded p-0.5 text-zinc-500 hover:bg-white/10 hover:text-zinc-200',
                  isLight ? chevronOpenLight : chevronOpen,
                )}
                aria-expanded={open}
                aria-label={open ? '折叠' : '展开'}
                onClick={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(n.id)) next.delete(n.id);
                    else next.add(n.id);
                    return next;
                  })
                }
              >
                <ChevronRight
                  className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
                />
              </button>
            ) : (
              <span className="inline-block w-5 shrink-0" aria-hidden />
            )}

            {selectable && n.path ? (
              <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 py-0.5 text-sm">
                <Checkbox
                  checked={value.has(n.path)}
                  onCheckedChange={() => onToggle(n.path!)}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <span className="min-w-0 flex-1">
                  <span className={cn('flex min-w-0 items-center gap-1.5', isLight ? 'text-slate-800' : 'text-zinc-200')}>
                    <NavIcon name={n.iconKey} className={cn('h-4 w-4', isLight ? 'text-slate-500' : 'text-zinc-400')} />
                    <span className="truncate">{n.title}</span>
                    {n.menuType === 'EXTERNAL' && (
                      <span className={cn('shrink-0 text-xs font-normal', isLight ? 'text-slate-500' : 'text-zinc-500')}>外链</span>
                    )}
                  </span>
                </span>
              </label>
            ) : (
              <div className={cn('flex min-w-0 flex-1 items-center gap-1.5 py-0.5 text-sm font-medium', isLight ? 'text-slate-600' : 'text-zinc-400')}>
                <NavIcon name={n.iconKey} className="h-4 w-4" />
                {n.title}
              </div>
            )}
          </div>
          {hasChildren && open && (
            <div>{renderNodes(n.children, depth + 1)}</div>
          )}
        </div>
      );
    });
  }

  return (
    <div
      className={cn(
        'h-[min(56vh,28rem)] space-y-0 overflow-y-auto rounded-lg border p-3',
        isLight ? 'border-slate-300 bg-white' : 'border-white/10',
      )}
    >
      {tree.length === 0 ? (
        <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>暂无菜单树数据</p>
      ) : (
        renderNodes(tree, 0)
      )}
    </div>
  );
}
